import { MouseEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import ProductImageSlider from '../../../components/common/ProductImageSlider';
import styles from './BundleItemProductCard.module.scss';
import drupalPubSubService from '../../../services/drupalPubSub';
import messages from './messages';
import {
  BEDDING_SIZE_NAME,
  PILLOW_SIZE_NAME,
  SELECTED_SIZE_REPLACEMENT_INDICATOR,
  SIZE_NAME,
} from './constants';
import {
  BundleItemProductCardProps,
  SelectedBundleVariants,
  SelectedVariantImages,
} from './types';
import { BundlePdpInStore } from '../../../slices/bundlePdp/types';

const BundleItemProductCard = ({
  bundleItem,
  isPrimaryItem,
  hidePrimaryDescription,
}: BundleItemProductCardProps) => {
  const { sanitize } = DOMPurify;
  const { publish } = drupalPubSubService;
  const { formatMessage } = useIntl();

  const { selectedVariantsForBundleItemProducts } = useSelector(
    (state: { bundlePdp: BundlePdpInStore }) => state.bundlePdp,
  );

  const [sliderImages, setSliderImages] = useState<SelectedVariantImages>([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const getSelectedVariantImages = ({
      selectedVariants,
      imagePropertyName = 'image',
    }: {
      selectedVariants: SelectedBundleVariants;
      imagePropertyName?: string;
    }) => {
      let variantImagesList;

      if (selectedVariants?.length) {
        variantImagesList = selectedVariants
          ?.map((variant, index) => ({
            imageUrl: variant[imagePropertyName as keyof typeof variant],
            imageLabel: `${bundleItem?.product.productType}-${index}`,
          }))
          // filter out non-string imageUrls and same images
          ?.reduce<SelectedVariantImages>((acc, curr) => {
            if (typeof curr.imageUrl !== 'string') return acc;
            return acc.some((imgObj) => imgObj.imageUrl === curr.imageUrl)
              ? acc
              : [
                  ...acc,
                  { imageUrl: curr.imageUrl, imageLabel: curr.imageLabel },
                ];
          }, []);
      } else {
        // select first variant img
        const bundleItemFirstVariant = bundleItem?.product.variants[0];
        variantImagesList = [
          {
            imageUrl:
              bundleItemFirstVariant?.[
                imagePropertyName as keyof typeof bundleItemFirstVariant
              ],
            imageLabel: `${bundleItem?.product.productType}-0`,
          },
        ];
      }

      return variantImagesList as SelectedVariantImages;
    };

    if (bundleItem?.product?.useVariantTeasers) {
      const selectedVariants =
        selectedVariantsForBundleItemProducts[bundleItem.product.key];
      const images = getSelectedVariantImages({
        selectedVariants,
        imagePropertyName: 'image',
      });
      setSliderImages(images);
    } else {
      const selectedVariants =
        selectedVariantsForBundleItemProducts[bundleItem?.product.key];
      const images = getSelectedVariantImages({
        selectedVariants,
        imagePropertyName: 'image',
      });
      setSliderImages(images);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleItem, selectedVariantsForBundleItemProducts, setSliderImages]);

  const isModal = bundleItem?.ctaLink?.href === '#';

  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (isModal) {
      e.preventDefault();
      publish({
        subscription: 'onShowModal',
        parameters: {
          micromodalTrigger: bundleItem?.ctaLink?.micromodalTrigger,
        },
      });
    }
  };

  const bundleProductKey = bundleItem?.product.key;
  const currentRelevantSelectedSize = () => {
    const selectedAttr = selectedVariantsForBundleItemProducts?.[
      bundleProductKey
    ]?.[0]?.attributes?.find(
      (attr) =>
        attr.name === BEDDING_SIZE_NAME ||
        attr.name === PILLOW_SIZE_NAME ||
        attr.name === SIZE_NAME,
    )?.label;
    return selectedAttr;
  };
  const reformatedIncludedLabel = bundleItem?.includedLabel?.replaceAll(
    SELECTED_SIZE_REPLACEMENT_INDICATOR,
    currentRelevantSelectedSize() ?? '',
  );

  return (
    <div
      className={classNames({
        [styles['bundle-accordian-container']]: true,
        [styles.opened]: showDropdown,
      })}
    >
      <div className={styles['bundle-accordian-header']}>
        {sliderImages && (
          <ProductImageSlider
            id={bundleItem?.product.key}
            images={sliderImages}
            currentImageIndex={0}
          />
        )}
        <div className={styles['bundle-title-link']}>
          {bundleItem?.quantity > 1
            ? `${bundleItem?.title} (${bundleItem?.quantity})`
            : bundleItem?.title}
        </div>
        {(!isPrimaryItem || !hidePrimaryDescription) && (
          <button
            type='button'
            data-testid={`bundle-dropdown-product-key-${bundleItem?.product.key}`}
            className={styles['bundle-title-dropdown-arrow']}
            aria-label={`${bundleItem?.title} accordian dropdown arrow`}
            onClick={(e) => {
              e.preventDefault();
              setShowDropdown((prevState) => !prevState);
            }}
            onKeyDown={(e) => {
              e.preventDefault();
              if (e.key === 'Enter') {
                setShowDropdown((prevState) => !prevState);
              }
            }}
          />
        )}
      </div>
      {(!isPrimaryItem || !hidePrimaryDescription) && showDropdown && (
        <div className={styles['bundle-accordian-dropdown']}>
          <div
            className={styles['product-description']}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitize(bundleItem?.productDescription ?? ''),
            }}
          />
          {bundleItem?.includedLabel?.length && (
            <>
              <div className={styles['included-label-title']}>
                {formatMessage(messages.bundleIncludedSectionTitle)}
              </div>
              <div className={styles['included-label']}>
                {reformatedIncludedLabel}
              </div>
            </>
          )}
          {bundleItem?.ctaLink?.href && (
            <div className={styles['bundle-link-container']}>
              <a
                href={bundleItem?.ctaLink.href}
                onClick={onClick}
                data-testid='more-info-link'
                className={styles['bundle-link']}
              >
                {bundleItem?.ctaLink.linkText}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BundleItemProductCard;
